import { useMemo } from "react";
import { SPACING } from "@disco/disco_core";
import { WIDGET_TYPE, WIDGET_VERSION } from "../conf";
import useDimensions from "./useDimensions";
import useExperience from "./useExperience";
import useMode from "./useMode";

export const LEAD_GEN_FEATURE = {
	SPACING: "SPACING",
	HEADING_BRAND_DIVIDER: "HEADING_BRAND_DIVIDER",
	BRAND_HEADER: "BRAND_HEADER",
	BRAND_COVER: "BRAND_COVER",
	BRAND_COVER_OFFER: "BRAND_COVER_OFFER",
	INLINE_PRODUCT_ROW: "INLINE_PRODUCT_ROW",
	ACTION_NAV: "ACTION_NAV",
	ACTION_BUTTON: "ACTION_BUTTON",
	ACTION_OFFER: "ACTION_OFFER",
	ACTION_OFFER_ICON: "ACTION_OFFER_ICON",
	PRODUCT_ROW: "PRODUCT_ROW",
	DOT_SWITCHER: "DOT_SWITCHER",
	NEXT_OFFER_BUTTON: "NEXT_OFFER_BUTTON",
};

export const SPACING_TARGET = {
	BRAND_TOP: "BRAND_TOP",
	INLINE_PRODUCT_TOP: "INLINE_PRODUCT_TOP",
	ACTION_MODULE_TOP: "ACTION_MODULE_TOP",
	PRODUCT_ROW_TOP: "PRODUCT_ROW_TOP",
};
const DEFAULT_SPACING_CONFIG = ({ isLargeWidget }) => ({
	[SPACING_TARGET.BRAND_TOP]: undefined,
	[SPACING_TARGET.INLINE_PRODUCT_TOP]: SPACING.SMALL,
	[SPACING_TARGET.ACTION_MODULE_TOP]: SPACING.SMALL,
	[SPACING_TARGET.PRODUCT_ROW_TOP]: isLargeWidget ? 24 : 20,
});

const DEFAULT_CONFIG = ({ isLargeWidget }) => ({
	[LEAD_GEN_FEATURE.SPACING]: DEFAULT_SPACING_CONFIG({ isLargeWidget }),
});
export const VERSION_FEATURE_MAP = ({ isLargeWidget, innerWidth }) => ({
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.1]]: {
		features: [
			LEAD_GEN_FEATURE.BRAND_COVER_OFFER,
			LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_BUTTON,
			LEAD_GEN_FEATURE.ACTION_NAV,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: 4,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_SHOW_COPY_ICON: true,
			},
		},
	},
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.2]]: {
		features: [
			LEAD_GEN_FEATURE.BRAND_COVER_OFFER,
			LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_BUTTON,
			LEAD_GEN_FEATURE.PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_NAV,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: 4,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_SHOW_COPY_ICON: true,
			},
		},
	},
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.3]]: {
		features: [
			LEAD_GEN_FEATURE.BRAND_COVER,
			LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_OFFER,
			LEAD_GEN_FEATURE.PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_NAV,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.BRAND_COVER]: {
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: innerWidth > 420 ? 4 : 3,
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_HIGHLIGHT_CTA: true,
				SHOULD_ANIMATE_NAV_TRANSITION: true,
			},
		},
	},
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.4]]: {
		features: [
			LEAD_GEN_FEATURE.BRAND_COVER,
			LEAD_GEN_FEATURE.ACTION_OFFER,
			LEAD_GEN_FEATURE.PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_NAV,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.BRAND_COVER]: {
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: innerWidth > 420 ? 4 : 3,
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_HIGHLIGHT_CTA: true,
				SHOULD_ANIMATE_NAV_TRANSITION: true,
			},
		},
	},
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.5]]: {
		features: [
			LEAD_GEN_FEATURE.HEADING_BRAND_DIVIDER,
			LEAD_GEN_FEATURE.BRAND_HEADER,
			LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_OFFER,
			LEAD_GEN_FEATURE.PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_NAV,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.SPACING]: {
				...DEFAULT_SPACING_CONFIG({ isLargeWidget }),
				[SPACING_TARGET.BRAND_TOP]: SPACING.REGULAR,
				[SPACING_TARGET.INLINE_PRODUCT_TOP]: SPACING.REGULAR,
				[SPACING_TARGET.ACTION_MODULE_TOP]: SPACING.REGULAR,
			},
			[LEAD_GEN_FEATURE.BRAND_HEADER]: {
				SHOULD_REDIRECT_ON_CLICK: true,
				MAX_BRAND_LOGO_IMG_SIZE: isLargeWidget ? 52 : 48,
			},
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: innerWidth > 420 ? 4 : 3,
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_HIGHLIGHT_CTA: true,
				SHOULD_ANIMATE_NAV_TRANSITION: true,
			},
		},
	},
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.51]]: {
		features: [
			LEAD_GEN_FEATURE.HEADING_BRAND_DIVIDER,
			LEAD_GEN_FEATURE.BRAND_HEADER,
			LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_OFFER,
			LEAD_GEN_FEATURE.PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_NAV,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.SPACING]: {
				...DEFAULT_SPACING_CONFIG({ isLargeWidget }),
				[SPACING_TARGET.BRAND_TOP]: SPACING.REGULAR,
				[SPACING_TARGET.INLINE_PRODUCT_TOP]: SPACING.REGULAR,
				[SPACING_TARGET.ACTION_MODULE_TOP]: SPACING.REGULAR,
			},
			[LEAD_GEN_FEATURE.BRAND_HEADER]: {
				SHOULD_REDIRECT_ON_CLICK: true,
				MAX_BRAND_LOGO_IMG_SIZE: isLargeWidget ? 52 : 48,
			},
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: innerWidth > 420 ? 4 : 3,
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_HIGHLIGHT_CTA: true,
				SHOULD_ANIMATE_NAV_TRANSITION: true,
				SHOULD_ANIMATE_FILL: true,
			},
		},
	},
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.55]]: {
		features: [
			LEAD_GEN_FEATURE.BRAND_COVER,
			LEAD_GEN_FEATURE.ACTION_OFFER,
			LEAD_GEN_FEATURE.PRODUCT_ROW,
			LEAD_GEN_FEATURE.ACTION_NAV,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.BRAND_COVER]: {
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: innerWidth > 420 ? 4 : 3,
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_HIGHLIGHT_CTA: true,
				SHOULD_ANIMATE_NAV_TRANSITION: true,
				SHOULD_ANIMATE_FILL: true,
			},
		},
	},
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.6]]: {
		features: [
			LEAD_GEN_FEATURE.BRAND_COVER,
			LEAD_GEN_FEATURE.ACTION_OFFER,
			LEAD_GEN_FEATURE.ACTION_NAV,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.BRAND_COVER]: {
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: innerWidth > 420 ? 4 : 3,
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_HIGHLIGHT_CTA: true,
				SHOULD_ANIMATE_NAV_TRANSITION: true,
				SHOULD_ANIMATE_FILL: true,
			},
		},
	},
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.65]]: {
		features: [
			LEAD_GEN_FEATURE.BRAND_COVER,
			LEAD_GEN_FEATURE.ACTION_OFFER,
			LEAD_GEN_FEATURE.PRODUCT_ROW,
			LEAD_GEN_FEATURE.DOT_SWITCHER,
			LEAD_GEN_FEATURE.NEXT_OFFER_BUTTON,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.BRAND_COVER]: {
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: innerWidth > 420 ? 4 : 3,
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_HIGHLIGHT_CTA: true,
				SHOULD_ANIMATE_NAV_TRANSITION: true,
				SHOULD_ANIMATE_FILL: true,
			},
		},
	},
	[WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.7]]: {
		features: [
			LEAD_GEN_FEATURE.BRAND_COVER,
			LEAD_GEN_FEATURE.ACTION_OFFER,
			LEAD_GEN_FEATURE.PRODUCT_ROW,
			LEAD_GEN_FEATURE.DOT_SWITCHER,
			LEAD_GEN_FEATURE.NEXT_OFFER_BUTTON,
		],
		config: {
			...DEFAULT_CONFIG({ isLargeWidget }),
			[LEAD_GEN_FEATURE.BRAND_COVER]: {
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW]: {
				MAX_PRODUCT_COUNT: innerWidth > 420 ? 4 : 3,
				SHOULD_REDIRECT_ON_CLICK: true,
			},
			[LEAD_GEN_FEATURE.ACTION_BUTTON]: {
				SHOULD_HIGHLIGHT_CTA: true,
				SHOULD_ANIMATE_NAV_TRANSITION: true,
				SHOULD_ANIMATE_FILL: true,
			},
		},
	},
});

const validateFeatureArgs = ({ feature, featureMap } = {}) => {
	if (!(feature in LEAD_GEN_FEATURE)) {
		console.warn("INVALID LEAD GEN FEATURE PASSED", feature);
		return false;
	}
	if (!featureMap || typeof featureMap !== "object") {
		console.warn("INVALID LEAD GEN FEATURE MAP PASSED", featureMap);
		return false;
	}
	const { features } = featureMap;
	if (!Array.isArray(features)) return false;

	return true;
};

const isFeatureActive = ({ feature, featureMap } = {}) => {
	if (!validateFeatureArgs({ feature, featureMap })) return false;

	const { features } = featureMap;

	return features.indexOf(feature) !== -1;
};

const getFeatureConfig = ({ feature, featureMap } = {}) => {
	if (!validateFeatureArgs({ feature, featureMap })) return false;

	const { config } = featureMap;
	if (typeof config !== "object" || !(feature in config)) return {};

	return config[feature];
};

const useLeadGenV0Config = () => {
	const { activeWidgetVersion } = useExperience();
	const { isInline } = useMode();
	const { shouldConsiderLargeWidget } = useDimensions();

	const isLargeWidget = useMemo(
		() => shouldConsiderLargeWidget(isInline),
		[isInline, shouldConsiderLargeWidget]
	);

	const currVersionFeatureMap = VERSION_FEATURE_MAP({
		isLargeWidget,
		innerWidth,
	})[activeWidgetVersion];

	const shouldShowProductRow = isFeatureActive({
		feature: LEAD_GEN_FEATURE.PRODUCT_ROW,
		featureMap: currVersionFeatureMap,
	});

	const _getFeatureConfig = ({ feature }) =>
		getFeatureConfig({ feature, featureMap: currVersionFeatureMap });
	const _isFeatureActive = ({ feature }) =>
		isFeatureActive({ feature, featureMap: currVersionFeatureMap });

	const spacingConfig = _getFeatureConfig({
		feature: LEAD_GEN_FEATURE.SPACING,
	});

	return {
		isLargeWidget, // Specifically: isLargeWidget for lead gen based on the requirements here
		currVersionFeatureMap,
		shouldShowProductRow,
		getFeatureConfig: _getFeatureConfig,
		isFeatureActive: _isFeatureActive,
		spacingConfig,
	};
};

export default useLeadGenV0Config;
